'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useClients } from '../hooks/useClients'
import { Button } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Textarea } from '@planner/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@planner/ui'
import { useTranslation } from 'react-i18next'
import type { Client, ClientContact } from '@planner/types'
import { Plus, Trash2, UserPlus } from 'lucide-react'

interface ClientFormProps {
  client?: Client | null
  onClose: () => void
  onSuccess?: (clientId: string) => void
  returnTo?: string
}

interface ContactFormData {
  full_name: string
  email: string
  phone: string
}

export function ClientForm({ client, onClose, onSuccess, returnTo }: ClientFormProps) {
  const router = useRouter()
  const { createClient, updateClient, addClientContact, updateClientContact, deleteClientContact } = useClients()
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    notes: '',
  })
  const [contacts, setContacts] = useState<Array<ContactFormData & { id?: string; isNew?: boolean; saving?: boolean }>>([])
  const [editingContactIndex, setEditingContactIndex] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [clientId, setClientId] = useState<string | null>(client?.id || null)

  useEffect(() => {
    if (client) {
      setClientId(client.id)
      setFormData({
        full_name: client.full_name || '',
        email: client.email || '',
        phone: client.phone || '',
        company: client.company || '',
        address: client.address || '',
        notes: client.notes || '',
      })
      // Load existing contacts
      if (client.client_contacts && client.client_contacts.length > 0) {
        setContacts(client.client_contacts.map(contact => ({
          id: contact.id,
          full_name: contact.full_name,
          email: contact.email,
          phone: contact.phone || '',
          isNew: false,
        })))
      }
    }
  }, [client])

  const addContact = () => {
    setContacts([...contacts, { full_name: '', email: '', phone: '', isNew: true }])
    setEditingContactIndex(contacts.length)
  }

  const updateContact = (index: number, field: keyof ContactFormData, value: string) => {
    const updated = [...contacts]
    updated[index] = { ...updated[index], [field]: value }
    setContacts(updated)
  }

  const removeContact = async (index: number) => {
    const contact = contacts[index]
    
    // If contact has an ID, delete it from database
    if (contact.id && clientId) {
      await deleteClientContact(contact.id)
    }
    
    setContacts(contacts.filter((_, i) => i !== index))
    if (editingContactIndex === index) {
      setEditingContactIndex(null)
    } else if (editingContactIndex !== null && editingContactIndex > index) {
      setEditingContactIndex(editingContactIndex - 1)
    }
  }

  const handleSaveContact = async (index: number) => {
    const contact = contacts[index]
    
    // Validate contact
    if (!contact.full_name || !contact.email) {
      setError('Osoba kontaktowa musi mieć imię i nazwisko oraz email')
      setSuccess(null)
      return
    }

    // Client must be saved first
    if (!clientId) {
      setError('Najpierw zapisz klienta, aby móc dodać osoby kontaktowe')
      setSuccess(null)
      return
    }

    // Set saving state for this contact
    setContacts(prev => prev.map((c, i) => i === index ? { ...c, saving: true } : c))
    setError(null)
    setSuccess(null)

    try {
      if (contact.id) {
        // Update existing contact
        const result = await updateClientContact(contact.id, {
          full_name: contact.full_name,
          email: contact.email,
          phone: contact.phone || undefined,
        })
        
        if (result.error) {
          setError(result.error)
          setContacts(prev => prev.map((c, i) => i === index ? { ...c, saving: false } : c))
          return
        }
        
        // Update local state - mark as saved
        setContacts(prev => prev.map((c, i) => 
          i === index ? { ...c, id: result.data?.id || contact.id, isNew: false, saving: false } : c
        ))
        setSuccess('Osoba kontaktowa została zaktualizowana')
        setTimeout(() => setSuccess(null), 3000)
      } else {
        // Add new contact
        const result = await addClientContact(clientId, {
          full_name: contact.full_name,
          email: contact.email,
          phone: contact.phone || undefined,
        })
        
        if (result.error) {
          setError(result.error)
          setContacts(prev => prev.map((c, i) => i === index ? { ...c, saving: false } : c))
          return
        }
        
        // Update local state with new ID
        setContacts(prev => prev.map((c, i) => 
          i === index ? { ...c, id: result.data?.id, isNew: false, saving: false } : c
        ))
        setSuccess('Osoba kontaktowa została dodana')
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err: any) {
      setError(err.message || 'Wystąpił błąd podczas zapisywania osoby kontaktowej')
      setContacts(prev => prev.map((c, i) => i === index ? { ...c, saving: false } : c))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    // Validate: at least main client must have full_name
    if (!formData.full_name || formData.full_name.trim() === '') {
      setError('Klient musi mieć imię i nazwisko')
      setLoading(false)
      return
    }

    try {
      let savedClientId: string

      if (client) {
        // Update existing client (only client data, not contacts)
        const result = await updateClient(client.id, formData)
        if (result.error) {
          setError(result.error)
          setLoading(false)
          return
        }
        savedClientId = client.id
        setClientId(savedClientId)
        setSuccess('Dane klienta zostały zaktualizowane')
        setTimeout(() => setSuccess(null), 3000)
      } else {
        // Create new client (without contacts - they are saved separately)
        const result = await createClient(formData, [])
        if (result.error) {
          setError(result.error)
          setLoading(false)
          return
        }
        if (!result.data) {
          setError('Nie udało się utworzyć klienta')
          setLoading(false)
          return
        }
        savedClientId = result.data.id
        setClientId(savedClientId)
        setSuccess('Klient został utworzony. Możesz teraz dodać osoby kontaktowe.')
        setTimeout(() => setSuccess(null), 5000)
      }

      // Success handling
      if (returnTo) {
        const separator = returnTo.includes('?') ? '&' : '?'
        router.push(`${returnTo}${separator}clientId=${savedClientId}`)
      } else if (onSuccess) {
        onSuccess(savedClientId)
        // Don't close - allow user to add contacts
      } else {
        // Don't auto-close - let user add contacts if needed
      }
      setLoading(false)
    } catch (err: any) {
      setError(err.message || 'Wystąpił błąd podczas zapisywania')
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="full_name">
            Imię i nazwisko *
          </Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
            placeholder="Jan Kowalski"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">
            Email *
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="jan.kowalski@example.com"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">
            Telefon
          </Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+48 123 456 789"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">
            Firma
          </Label>
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            placeholder="Nazwa firmy"
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="address">
            Adres
          </Label>
          <Input
            id="address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="Ulica, kod pocztowy, miasto"
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="notes">
            Notatki
          </Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Dodatkowe informacje o kliencie..."
            rows={4}
          />
        </div>
      </div>

      {/* Contact Persons Section */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Osoby kontaktowe</h3>
            <p className="text-sm text-muted-foreground">
              {clientId 
                ? 'Dodaj osoby kontaktowe'
                : 'Najpierw zapisz klienta, aby móc dodać osoby kontaktowe'}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addContact}
            disabled={!clientId}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Dodaj osobę
          </Button>
        </div>

        {contacts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
            <UserPlus className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Brak osób kontaktowych</p>
            <p className="text-xs mt-1">Kliknij "Dodaj osobę", aby dodać pierwszą osobę kontaktową</p>
          </div>
        ) : (
          <div className="space-y-3">
            {contacts.map((contact, index) => (
              <Card key={index} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Osoba kontaktowa {index + 1}
                    </CardTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeContact(index)}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`contact_name_${index}`}>
                        Imię i nazwisko *
                      </Label>
                      <Input
                        id={`contact_name_${index}`}
                        value={contact.full_name}
                        onChange={(e) => updateContact(index, 'full_name', e.target.value)}
                        required
                        placeholder="Jan Kowalski"
                        disabled={contact.saving}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`contact_email_${index}`}>
                        Email *
                      </Label>
                      <Input
                        id={`contact_email_${index}`}
                        type="email"
                        value={contact.email}
                        onChange={(e) => updateContact(index, 'email', e.target.value)}
                        required
                        placeholder="jan.kowalski@example.com"
                        disabled={contact.saving}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`contact_phone_${index}`}>
                        Telefon
                      </Label>
                      <Input
                        id={`contact_phone_${index}`}
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        placeholder="+48 123 456 789"
                        disabled={contact.saving}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <Button
                      type="button"
                      onClick={() => handleSaveContact(index)}
                      disabled={contact.saving || !clientId || !contact.full_name || !contact.email}
                      size="sm"
                    >
                      {contact.saving ? 'Zapisywanie...' : contact.id ? 'Zapisz zmiany' : 'Zapisz osobę'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 p-4 rounded-md">
          {error}
        </div>
      )}
      {success && (
        <div className="text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 p-4 rounded-md">
          {success}
        </div>
      )}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel', 'Anuluj')}
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? t('common.loading', 'Zapisywanie...') : t('common.save', 'Zapisz klienta')}
        </Button>
      </div>
    </form>
  )
}

